<template>
  <Breadcrumbs
    title="Create Shedule"
    :breadcrumbs="[
      { label: 'Stock', path: '' },
      { label: 'Shedule List', path: '/delivery-shedule' },
      { label: 'Dispatch Locations', path: '/dispatch-locations' },
    ]"
  />

  <div class="container-fluid">
    <div class="row project-cards">
      <div class="col-sm-12">
        <div class="card dispatch-card">

          <!-- ── Loading State ── -->
          <div v-if="isLoading" class="state-box">
            <i class="fa fa-spinner fa-spin"></i> Loading routes...
          </div>

          <!-- ── Error State ── -->
          <div v-else-if="loadError" class="state-box state-error">
            <i class="fa fa-exclamation-triangle"></i> {{ loadError }}
            <button class="btn-retry" @click="loadRoutesWithDealers">Retry</button>
          </div>

          <!-- ── Main Form ── -->
          <template v-else>

            <!-- Row 1: Date | Delivery Window -->
            <div class="form-row-grid">
              <div class="form-field">
                <label class="field-label">Date:</label>
                <div class="date-input-wrapper">
                  <input type="date" v-model="selectedDate" class="field-input" />
                  <span class="calendar-icon"><i class="fa fa-calendar"></i></span>
                </div>
              </div>

              <div class="form-field">
                <label class="field-label">Delivery Window:</label>
                <select v-model="selectedWindow" class="field-input field-select">
                  <option value="">Select Window</option>
                  <option v-for="w in deliveryWindows" :key="w.id" :value="w.id">
                    {{ w.label }}
                  </option>
                </select>
              </div>
            </div>

            <!-- ── Routes + Dealers Section ── -->
            <div class="section-block">
              <div class="section-header">
                <span class="section-title">Routes &amp; Dealers <span class="required">*</span></span>
              </div>

              <!-- ── Add Route row — SEARCHABLE ── -->
              <div class="add-row">
                <div class="searchable-wrapper" v-click-outside="() => closeDropdown('route')">
                  <div class="search-input-box" @click="toggleDropdown('route')">
                    <span v-if="!pendingRoute" class="placeholder-text">
                      {{ availableRoutes.length === 0 ? 'No more routes available' : 'Select Route to Add' }}
                    </span>
                    <span v-else class="selected-text">{{ getRouteName(pendingRoute) }}</span>
                    <i class="fa fa-chevron-down chevron" :class="{ open: routeDropdownOpen }"></i>
                  </div>

                  <div class="dropdown-panel" v-if="routeDropdownOpen">
                    <div class="dropdown-search">
                      <i class="fa fa-search search-icon"></i>
                      <input
                        ref="routeSearchInput"
                        v-model="routeSearch"
                        placeholder="Search route..."
                        class="dropdown-search-input"
                        @click.stop
                      />
                    </div>
                    <ul class="dropdown-list">
                      <li
                        v-if="filteredAvailableRoutes.length === 0"
                        class="dropdown-empty"
                      >No routes found</li>
                      <li
                        v-for="route in filteredAvailableRoutes"
                        :key="route.route_id"
                        class="dropdown-item"
                        :class="{ active: pendingRoute === route.route_id }"
                        @click="selectRoute(route.route_id)"
                      >
                        <i class="fa fa-map-marker item-icon"></i>
                        {{ route.route_name }}
                      </li>
                    </ul>
                  </div>
                </div>

                <button class="btn-add-route" @click="addRoute">
                  <i class="fa fa-plus"></i> Add Route
                </button>
              </div>

              <!-- Empty state -->
              <p class="empty-hint" v-if="routeEntries.length === 0">
                No routes added yet. Add a route to assign dealers.
              </p>

              <!-- Route Cards -->
              <div class="route-cards-list" v-else>
                <div class="route-card" v-for="entry in routeEntries" :key="entry.routeId">

                  <!-- Route card header -->
                  <div class="route-card-header">
                    <div class="route-badge">
                      <i class="fa fa-map-marker route-icon"></i>
                      <span class="route-name">{{ getRouteName(entry.routeId) }}</span>
                    </div>
                    <button class="btn-remove-route" @click="removeRoute(entry.routeId)">
                      <i class="fa fa-trash"></i> Remove Route
                    </button>
                  </div>

                  <!-- Dealer assignment area -->
                  <div class="dealer-area">
                    <p class="dealer-area-label">Dealers assigned to this route:</p>

                    <!-- ── Add Dealer — SEARCHABLE ── -->
                    <div class="dealer-add-row">
                      <div class="searchable-wrapper" v-click-outside="() => closeDealerDropdown(entry)">
                        <div class="search-input-box" @click="toggleDealerDropdown(entry)">
                          <span v-if="!entry.pendingDealer" class="placeholder-text">
                            {{ getAvailableDealersForRoute(entry).length === 0 ? 'All dealers added' : 'Select Dealer' }}
                          </span>
                          <span v-else class="selected-text">{{ getDealerName(entry.routeId, entry.pendingDealer) }}</span>
                          <i class="fa fa-chevron-down chevron" :class="{ open: entry.dealerDropdownOpen }"></i>
                        </div>

                        <div class="dropdown-panel" v-if="entry.dealerDropdownOpen">
                          <div class="dropdown-search">
                            <i class="fa fa-search search-icon"></i>
                            <input
                              v-model="entry.dealerSearch"
                              placeholder="Search dealer..."
                              class="dropdown-search-input"
                              @click.stop
                            />
                          </div>
                          <ul class="dropdown-list">
                            <li
                              v-if="getFilteredDealers(entry).length === 0"
                              class="dropdown-empty"
                            >No dealers found</li>
                            <li
                              v-for="dealer in getFilteredDealers(entry)"
                              :key="dealer.dealer_profile_id"
                              class="dropdown-item"
                              :class="{ active: entry.pendingDealer === dealer.dealer_profile_id }"
                              @click="selectDealer(entry, dealer.dealer_profile_id)"
                            >
                              <i class="fa fa-user item-icon"></i>
                              {{ dealer.business_name }}
                            </li>
                          </ul>
                        </div>
                      </div>

                      <button class="btn-add-dealer" @click="addDealer(entry)">
                        <i class="fa fa-plus"></i> Add
                      </button>
                    </div>

                    <!-- No dealers yet -->
                    <p class="no-dealers-hint" v-if="entry.dealers.length === 0">
                      No dealers assigned yet.
                    </p>

                    <!-- Dealer pills -->
                    <div class="dealer-pill-list" v-else>
                      <div
                        class="dealer-pill"
                        v-for="dealerId in entry.dealers"
                        :key="dealerId"
                      >
                        <i class="fa fa-user-circle dealer-pill-icon"></i>
                        <span>{{ getDealerName(entry.routeId, dealerId) }}</span>
                        <button class="pill-remove" @click="removeDealer(entry, dealerId)" title="Remove">
                          <i class="fa fa-times"></i>
                        </button>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>

            <!-- Save Button -->
            <div class="save-row">
              <button class="btn-save" @click="handleSave">Save</button>
            </div>

          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { fetchRoutesWithDealers, saveDailySchedule } from '@/services/dataService';
import { useToast } from 'vue-toastification';

// ── Click-outside directive ──────────────────────────────────
const clickOutside = {
  beforeMount(el, binding) {
    el._clickOutsideHandler = (event) => {
      if (!el.contains(event.target)) binding.value(event);
    };
    document.addEventListener('click', el._clickOutsideHandler);
  },
  unmounted(el) {
    document.removeEventListener('click', el._clickOutsideHandler);
  },
};

export default {
  name: 'DispatchLocations',

  directives: { clickOutside },

  setup() {
    const toast = useToast();
    return { toast };
  },

  data() {
    const today = new Date().toISOString().split('T')[0];
    return {
      selectedDate:      today,
      selectedWindow:    '',
      pendingRoute:      '',
      routeSearch:       '',
      routeDropdownOpen: false,

      apiRoutesData: [],
      isLoading:     false,
      loadError:     null,
      routeEntries:  [],

      deliveryWindows: [
        { id: 1, label: 'Morning (6:00 AM - 12:00 PM)' },
        { id: 2, label: 'Afternoon (12:00 PM - 5:00 PM)' },
        { id: 3, label: 'Evening (5:00 PM - 9:00 PM)' },
        { id: 4, label: 'Full Day' },
      ],
    };
  },

  computed: {
    availableRoutes() {
      const usedIds = this.routeEntries.map(e => e.routeId);
      return this.apiRoutesData.filter(
        r => !usedIds.includes(r.route_id) && parseInt(r.dealer_count) > 0
      );
    },

    filteredAvailableRoutes() {
      const q = this.routeSearch.toLowerCase().trim();
      if (!q) return this.availableRoutes;
      return this.availableRoutes.filter(r =>
        r.route_name.toLowerCase().includes(q)
      );
    },
  },

  methods: {

    // ── Route dropdown ───────────────────────────────────────
    toggleDropdown(type) {
      if (type === 'route') {
        this.routeDropdownOpen = !this.routeDropdownOpen;
        if (this.routeDropdownOpen) {
          this.routeSearch = '';
          this.$nextTick(() => this.$refs.routeSearchInput?.focus());
        }
      }
    },

    closeDropdown(type) {
      if (type === 'route') this.routeDropdownOpen = false;
    },

    selectRoute(routeId) {
      this.pendingRoute      = routeId;
      this.routeDropdownOpen = false;
      this.routeSearch       = '';
    },

    // ── Dealer dropdown ──────────────────────────────────────
    toggleDealerDropdown(entry) {
      entry.dealerDropdownOpen = !entry.dealerDropdownOpen;
      if (entry.dealerDropdownOpen) entry.dealerSearch = '';
    },

    closeDealerDropdown(entry) {
      entry.dealerDropdownOpen = false;
    },

    selectDealer(entry, dealerId) {
      entry.pendingDealer      = dealerId;
      entry.dealerDropdownOpen = false;
      entry.dealerSearch       = '';
    },

    getFilteredDealers(entry) {
      const available = this.getAvailableDealersForRoute(entry);
      const q = (entry.dealerSearch || '').toLowerCase().trim();
      if (!q) return available;
      return available.filter(d =>
        d.business_name.toLowerCase().includes(q)
      );
    },

    // ── API ──────────────────────────────────────────────────
    async loadRoutesWithDealers() {
      this.isLoading = true;
      this.loadError = null;
      try {
        const token         = localStorage.getItem('token_set');
        const user          = JSON.parse(localStorage.getItem('User'));
        const distributorId = user?.profile_id;

        const response = await fetchRoutesWithDealers(token, distributorId);

        if (response.data.status === 'success') {
          this.apiRoutesData = response.data.data.routes_with_dealers;
        } else {
          this.loadError = response.data.message || 'Failed to load routes.';
        }
      } catch (err) {
        console.error('Failed to load routes with dealers:', err);
        this.loadError = 'Could not connect to the server. Please try again.';
      } finally {
        this.isLoading = false;
      }
    },

    // ── Routes ───────────────────────────────────────────────
    addRoute() {
      if (!this.pendingRoute) {
        this.toast.warning('Please select a route.');
        return;
      }
      this.routeEntries.push({
        routeId:           this.pendingRoute,
        pendingDealer:     '',
        dealers:           [],
        dealerSearch:      '',
        dealerDropdownOpen: false,
      });
      this.pendingRoute      = '';
      this.routeDropdownOpen = false;
    },

    removeRoute(routeId) {
      this.routeEntries = this.routeEntries.filter(e => e.routeId !== routeId);
    },

    getRouteName(routeId) {
      return this.apiRoutesData.find(r => r.route_id === routeId)?.route_name ?? routeId;
    },

    // ── Dealers ──────────────────────────────────────────────
    getDealerPool(routeId) {
      return this.apiRoutesData.find(r => r.route_id === routeId)?.dealers ?? [];
    },

    getAvailableDealersForRoute(entry) {
      return this.getDealerPool(entry.routeId)
        .filter(d => !entry.dealers.includes(d.dealer_profile_id));
    },

    addDealer(entry) {
      if (!entry.pendingDealer) {
        this.toast.warning('Please select a dealer.');
        return;
      }
      entry.dealers.push(entry.pendingDealer);
      entry.pendingDealer      = '';
      entry.dealerDropdownOpen = false;
    },

    removeDealer(entry, dealerId) {
      entry.dealers = entry.dealers.filter(id => id !== dealerId);
    },

    getDealerName(routeId, dealerId) {
      const dealer = this.getDealerPool(routeId).find(d => d.dealer_profile_id === dealerId);
      return dealer?.business_name ?? dealerId;
    },

    // ── Save ─────────────────────────────────────────────────
    async handleSave() {
      if (!this.selectedDate) {
        this.toast.warning('Please select a date.');
        return;
      }
      if (this.routeEntries.length === 0) {
        this.toast.warning('Please add at least one route.');
        return;
      }
      if (this.routeEntries.some(e => e.dealers.length === 0)) {
        this.toast.warning('Each route must have at least one dealer assigned.');
        return;
      }

      const user  = JSON.parse(localStorage.getItem('User'));
      const token = localStorage.getItem('token_set');

      const payload = {
        distributor_profile_id: parseInt(user?.profile_id),
        company_id:             parseInt(user?.company_id),
        date_schedule:          this.selectedDate,
        delivery_window:        this.deliveryWindows.find(w => w.id === this.selectedWindow)?.label ?? '',
        created_by:             parseInt(user?.profile_id),
        dealers: this.routeEntries.flatMap(e =>
          e.dealers.map(dealerId => ({ dealer_id: parseInt(dealerId) }))
        ),
      };

      try {
        const response = await saveDailySchedule(token, payload);
        if (response.data.status === 'success') {
          this.toast.success('Schedule saved successfully!');
          this.routeEntries   = [];
          this.selectedWindow = '';
        } else {
          this.toast.error(response.data.message || 'Failed to save.');
        }
      } catch (err) {
        console.error('Save failed:', err);
        this.toast.error('Something went wrong. Please try again.');
      }
    },
  },

  mounted() {
    this.loadRoutesWithDealers();
  },
};
</script>

<style scoped lang="scss">
.dispatch-card {
  padding: 28px 32px 36px;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

.state-box {
  padding: 40px;
  text-align: center;
  color: #6b7280;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  i { font-size: 18px; }
  &.state-error { color: #dc2626; }
}

.btn-retry {
  background: #dc2626;
  color: #fff;
  border: none;
  border-radius: 5px;
  padding: 5px 14px;
  font-size: 13px;
  cursor: pointer;
  margin-left: 8px;
  &:hover { background: #b91c1c; }
}

.form-row-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 28px;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field-label {
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
}

.required { color: #e53e3e; margin-left: 2px; }

.field-input {
  height: 44px;
  padding: 0 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  color: #374151;
  background: #fff;
  width: 100%;
  box-sizing: border-box;
  transition: border-color 0.2s, box-shadow 0.2s;
  &:focus {
    outline: none;
    border-color: #4a7fc1;
    box-shadow: 0 0 0 3px rgba(74, 127, 193, 0.15);
  }
}

.field-select {
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%236b7280' d='M1 1l5 5 5-5'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  padding-right: 32px;
  cursor: pointer;
}

.date-input-wrapper {
  position: relative;
  .field-input { padding-right: 38px; }
  .calendar-icon {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #6b7280;
    pointer-events: none;
  }
}

/* ── Searchable Dropdown ── */
.searchable-wrapper {
  position: relative;
  width: 100%;
}

.search-input-box {
  height: 44px;
  padding: 0 36px 0 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  background: #fff;
  display: flex;
  align-items: center;
  cursor: pointer;
  position: relative;
  transition: border-color 0.2s, box-shadow 0.2s;
  user-select: none;

  &:hover {
    border-color: #4a7fc1;
  }

  .placeholder-text {
    color: #9ca3af;
    font-size: 14px;
  }

  .selected-text {
    color: #374151;
    font-size: 14px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .chevron {
    position: absolute;
    right: 12px;
    color: #6b7280;
    font-size: 11px;
    transition: transform 0.2s;
    &.open { transform: rotate(180deg); }
  }
}

.dropdown-panel {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  z-index: 999;
  overflow: hidden;
}

.dropdown-search {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border-bottom: 1px solid #f0f0f0;
  gap: 8px;

  .search-icon {
    color: #9ca3af;
    font-size: 13px;
    flex-shrink: 0;
  }

  .dropdown-search-input {
    border: none;
    outline: none;
    font-size: 13px;
    color: #374151;
    width: 100%;
    background: transparent;

    &::placeholder { color: #9ca3af; }
  }
}

.dropdown-list {
  list-style: none;
  margin: 0;
  padding: 6px 0;
  max-height: 220px;
  overflow-y: auto;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 4px;
  }
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 14px;
  font-size: 13px;
  color: #374151;
  cursor: pointer;
  transition: background 0.15s;

  .item-icon {
    color: #9ca3af;
    font-size: 12px;
    width: 14px;
    text-align: center;
    flex-shrink: 0;
  }

  &:hover {
    background: #f0f7ff;
    color: #1e3a5f;
    .item-icon { color: #3b72b8; }
  }

  &.active {
    background: #eef2f9;
    color: #1e3a5f;
    font-weight: 600;
    .item-icon { color: #3b72b8; }
  }
}

.dropdown-empty {
  padding: 12px 14px;
  font-size: 13px;
  color: #9ca3af;
  font-style: italic;
  text-align: center;
}

/* ── Rest unchanged ── */
.section-block {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 22px 24px;
  margin-bottom: 28px;
  background: #f9fafb;
}

.section-header { margin-bottom: 16px; }

.section-title {
  font-size: 15px;
  font-weight: 700;
  color: #1f2937;
}

.add-row {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 14px;
  align-items: center;
  margin-bottom: 20px;
}

.btn-add-route {
  height: 44px;
  padding: 0 20px;
  background: #3b72b8;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background 0.2s;
  &:hover { background: #2d5f9e; }
  &:active { background: #245090; }
}

.empty-hint {
  font-size: 13px;
  color: #9ca3af;
  font-style: italic;
  margin: 0;
}

.route-cards-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.route-card {
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  overflow: visible;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.route-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #eef2f9;
  border-bottom: 1px solid #d1d5db;
  padding: 12px 18px;
  border-radius: 8px 8px 0 0;
}

.route-badge {
  display: flex;
  align-items: center;
  gap: 10px;
}

.route-icon { color: #3b72b8; font-size: 16px; }

.route-name {
  font-size: 14px;
  font-weight: 700;
  color: #1e3a5f;
}

.btn-remove-route {
  background: none;
  border: 1px solid #fca5a5;
  color: #dc2626;
  border-radius: 5px;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: background 0.2s;
  &:hover { background: #fee2e2; }
}

.dealer-area { padding: 16px 18px 18px; }

.dealer-area-label {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin: 0 0 12px;
}

.dealer-add-row {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 12px;
  align-items: center;
  margin-bottom: 14px;
}

.btn-add-dealer {
  height: 40px;
  padding: 0 18px;
  background: #059669;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: background 0.2s;
  &:hover { background: #047857; }
  &:active { background: #065f46; }
}

.no-dealers-hint {
  font-size: 12px;
  color: #9ca3af;
  font-style: italic;
  margin: 0;
}

.dealer-pill-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.dealer-pill {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: 20px;
  padding: 5px 10px 5px 12px;
  font-size: 13px;
  font-weight: 500;
  color: #166534;

  .dealer-pill-icon { font-size: 13px; color: #16a34a; }

  .pill-remove {
    background: none;
    border: none;
    color: #86efac;
    cursor: pointer;
    padding: 0;
    font-size: 11px;
    line-height: 1;
    display: flex;
    align-items: center;
    transition: color 0.15s;
    &:hover { color: #dc2626; }
  }
}

.save-row { display: flex; justify-content: center; }

.btn-save {
  width: 100%;
  max-width: 480px;
  height: 48px;
  background: #3a8a4e;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  letter-spacing: 0.5px;
  transition: background 0.2s, transform 0.1s;
  &:hover { background: #2e7040; }
  &:active { background: #245c34; transform: scale(0.99); }
}

@media (max-width: 768px) {
  .form-row-grid,
  .add-row,
  .dealer-add-row {
    grid-template-columns: 1fr;
  }
  .btn-add-route,
  .btn-add-dealer {
    width: 100%;
    justify-content: center;
  }
  .route-card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
}
</style>